import React from "react";
import formatDate from "../../../utils/formatDate";
import Button from "../../../components/Button/Button";
import { FaCreditCard, FaCoins, FaBell } from "react-icons/fa";
import { toAbsoluteArticlePhotoUrl } from "../../../services/articlesPhotoServices";

function hasEcheanceTag(msg = "") {
  const clean = msg
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return clean.includes("echeance");
}

const ArticleCard = ({
  articles,
  getStatusColor,
  defaultPhotoUrl,
  handlePayerClick,
  handlePayerEnPlusieursFoisClick,
  payingId,
  handleNotifyClick,
  notifyingId,
  isAlreadyNotified,
}) => {
  const FALLBACK = "/img/no-image.jpg";

  return (
    <div className="sm:hidden">
      {articles.length > 0 ? (
        articles.map((article) => {
          const isDisabled = article.autorisation_achat === false;
          const canShowButtons =
            ["Non Réglé", "En Attente"].includes(article.statut_reglement);

          // Image: résout correctement les URLs relatives (ex: /uploads/...)
          const rawPhoto =
            article.Article && article.Article.photo_url
              ? article.Article.photo_url
              : defaultPhotoUrl;

          const imgSrc = rawPhoto
            ? toAbsoluteArticlePhotoUrl(rawPhoto)
            : FALLBACK;

          const imgAlt =
            article.article_id === null
              ? "Règlement Licence-Cotisation"
              : article.Article?.nom || "Article";

          const isInstallment = hasEcheanceTag(article.message_achat);
          const canShowInstallmentButton =
            article.article_id === null && !isInstallment;

          return (
            <div
              key={article.id}
              className="bg-white dark:bg-slate-200 shadow-md rounded-lg mb-4 p-4 flex flex-col"
            >
              {/* Image */}
              <div className="w-full flex justify-center mb-4">
                <img
                  src={imgSrc}
                  alt={imgAlt}
                  className="w-auto h-32 object-cover rounded-lg"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK;
                  }}
                />
              </div>

              {/* Texte & infos */}
              <div className="flex-1">
                <div className="text-sm text-gray-500">
                  {formatDate(article.date_achat)}
                </div>

                <div className="text-sm text-gray-500 font-semibold">
                  {`${article.quantite} x ${
                    article.article_id === null
                      ? "Règlement Licence-Cotisation"
                      : article.Article
                      ? article.Article.nom
                      : "Nom non disponible"
                  }`}
                </div>

                <div className="text-xs italic text-gray-800">
                  {article.message_achat}
                </div>

                <div className="text-normal font-bold text-gray-500">
                  {`${article.prix_total} €`}
                </div>

                <span
                  className={`inline-block my-2 px-3 py-1 rounded-full font-semibold text-xs ${getStatusColor(
                    article.statut_reglement
                  )}`}
                >
                  {article.statut_reglement}
                </span>

                {/* Boutons paiement */}
                {canShowButtons && (
                  <div className="flex gap-2 mt-2 items-center">
                    {isDisabled && (
                      <span className="text-xs text-red-700 italic self-center">
                        Paiement non activé
                      </span>
                    )}

                    <Button
                      text={
                        payingId === article.id ? "Redirection…" : "Payer"
                      }
                      icon={FaCreditCard}
                      onClick={
                        isDisabled ? undefined : () => handlePayerClick(article.id)
                      }
                      disabled={isDisabled || payingId === article.id}
                      className={`gap-2 ${
                        isDisabled ? "opacity-40 cursor-not-allowed" : ""
                      }`}
                    />

                    {/* Masqué dès que c’est une échéance */}
                    {canShowInstallmentButton && (
                      <Button
                        text="Payer en plusieurs fois"
                        icon={FaCoins}
                        onClick={
                          isDisabled
                            ? undefined
                            : () => handlePayerEnPlusieursFoisClick(article.id)
                        }
                        disabled={isDisabled}
                        className={`gap-2 whitespace-nowrap ${
                          isDisabled ? "opacity-40 cursor-not-allowed" : ""
                        }`}
                      />
                    )}
                  </div>
                )}

                {/* 🔔 Bouton “Prévenir l’admin” si payé */}
                {article.statut_reglement === "Réglé" &&
                  (isAlreadyNotified?.(article.id) ? (
                    <span className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      Admin prévenu
                    </span>
                  ) : (
                    <div className="mt-2">
                      <Button
                        text={
                          notifyingId === article.id
                            ? "Envoi…"
                            : "Prévenir l'admin"
                        }
                        icon={FaBell}
                        onClick={() => handleNotifyClick?.(article)}
                        disabled={notifyingId === article.id}
                        className="gap-2"
                      />
                    </div>
                  ))}
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center py-4 px-4 dark:text-black">
          Pas d'articles commandés
        </div>
      )}
    </div>
  );
};

export default ArticleCard;
